import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBold, FaItalic, FaPlus, FaTrash } from "react-icons/fa6";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import ConfirmModal from "../../../public/ConfirmModal";
import NoTranslate from "../../../atoms/NoTranslate";
import WhatsAppImageUpload from "../../../atoms/WhatsAppImageUpload";
import { bodyEdgesOnVariable } from "../../../../utils/whatsappText";
import WhatsAppMessagePreview, {
  PreviewButton,
} from "../providers/WhatsAppMessagePreview";
import useAuth from "../../../../hooks/useAuth";
import {
  useCreateWhatsappTemplate,
  useDeleteWhatsappTemplate,
  useGetWhatsappTemplates,
  useRefreshWhatsappTemplateStatus,
  useSubmitWhatsappTemplate,
  useUpdateWhatsappTemplate,
} from "../../../../hooks/messages";

interface ManageWhatsAppTemplatesModalProps {
  show: boolean;
  onClose: () => void;
}

type TemplateButton =
  | {
      type: "url";
      title: string;
      url: string;
      dynamic_suffix?: boolean;
      url_sample_suffix?: string;
    }
  | { type: "phone"; title: string; phone: string }
  | { type: "payment"; title: string }
  | { type: "opt_out" };

interface CustomTemplate {
  id: string;
  name: string;
  category: "marketing" | "utility";
  language: "fr" | "en";
  body: string;
  variables_count: number;
  header: "none" | "image";
  header_sample_url: string | null;
  body_samples: string[] | null;
  buttons: TemplateButton[];
  url_variable_index: number | null;
  status: "draft" | "pending" | "approved" | "rejected" | "submission_failed";
  rejection_reason: string | null;
}

// Utility templates only: WhatsApp forbids mixing CTA buttons with the marketing opt-out.
interface FormButton {
  type: "url" | "phone" | "payment";
  title: string;
  url: string;
  dynamic_suffix: boolean;
  url_sample_suffix: string;
  phone: string;
}

const MAX_CTA_BUTTONS = 2;
const BUTTON_TITLE_MAX = 25;
const E164_PATTERN = /^\+[1-9]\d{6,14}$/;
const HTTPS_PATTERN = /^https:\/\/.+/i;

const emptyButton: FormButton = {
  type: "url",
  title: "",
  url: "",
  dynamic_suffix: false,
  url_sample_suffix: "",
  phone: "",
};

const CTA_TYPES = ["url", "phone", "payment"];

const formButtonsOf = (
  buttons: TemplateButton[] | null | undefined
): FormButton[] =>
  (buttons ?? [])
    .filter(
      (
        b
      ): b is Extract<TemplateButton, { type: "url" | "phone" | "payment" }> =>
        CTA_TYPES.includes(b.type)
    )
    .map((b) => ({
      type: b.type,
      title: b.title,
      url: b.type === "url" ? b.url : "",
      dynamic_suffix: b.type === "url" ? Boolean(b.dynamic_suffix) : false,
      url_sample_suffix: b.type === "url" ? b.url_sample_suffix ?? "" : "",
      phone: b.type === "phone" ? b.phone : "",
    }));

const previewButtonsOf = (
  buttons: Array<{ type: string; title?: string }> | null | undefined
): PreviewButton[] =>
  (buttons ?? [])
    .filter((b) => CTA_TYPES.includes(b.type))
    .map((b) => ({
      // A payment button reaches the client as a link button.
      type: b.type === "phone" ? ("phone" as const) : ("url" as const),
      title: b.title ?? "",
    }));

const RESUBMITTABLE_STATUSES = ["draft", "rejected", "submission_failed"];

const fieldClass =
  "w-full rounded-md border border-gray-300 p-2.5 text-base md:py-2 md:text-sm text-gray-800 focus:border-blue-500 focus:ring-blue-500";

const PLACEHOLDER_REGEX = /\{\{\s*(\d+)\s*\}\}/g;

const countContiguousPlaceholders = (
  body: string
): { count: number; valid: boolean } => {
  const found = new Set<number>();
  let match: RegExpExecArray | null;
  PLACEHOLDER_REGEX.lastIndex = 0;
  while ((match = PLACEHOLDER_REGEX.exec(body)) !== null) {
    found.add(Number(match[1]));
  }
  const ordered = Array.from(found).sort((a, b) => a - b);
  const valid = ordered.every((value, index) => value === index + 1);
  return { count: ordered.length, valid };
};

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  submission_failed: "bg-red-100 text-red-700",
};

interface FormState {
  name: string;
  category: "marketing" | "utility";
  language: "fr" | "en";
  body: string;
  // Meta reviews the template with these substituted, so they must read like real values.
  body_samples: string[];
  header: "none" | "image";
  header_sample_url: string;
  buttons: FormButton[];
}

const emptyForm: FormState = {
  name: "",
  category: "marketing",
  language: "fr",
  body: "",
  body_samples: [],
  header: "none",
  header_sample_url: "",
  buttons: [],
};

const ManageWhatsAppTemplatesModal: React.FC<
  ManageWhatsAppTemplatesModalProps
> = ({ show, onClose }) => {
  const { t } = useTranslation();
  const { customer_identification, user } = useAuth();
  const entity_identification = user.entity_identification;

  const { data, isLoading } = useGetWhatsappTemplates(
    customer_identification,
    entity_identification,
    show
  );
  const { mutate: createTemplate, isLoading: isCreating } =
    useCreateWhatsappTemplate();
  const { mutate: updateTemplate, isLoading: isUpdating } =
    useUpdateWhatsappTemplate();
  const { mutate: submitTemplate, isLoading: isSubmitting } =
    useSubmitWhatsappTemplate();
  const { mutate: deleteTemplate, isLoading: isDeleting } =
    useDeleteWhatsappTemplate();
  const { mutate: refreshStatus, isLoading: isRefreshing } =
    useRefreshWhatsappTemplateStatus();

  const templates: CustomTemplate[] = useMemo(
    () => data?.templates ?? [],
    [data]
  );
  const remaining: number = data?.remaining ?? 0;
  const maxTemplates: number = data?.max ?? 2;

  const [mode, setMode] = useState<"list" | "form">("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [templateToDelete, setTemplateToDelete] =
    useState<CustomTemplate | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const placeholders = useMemo(
    () => countContiguousPlaceholders(form.body),
    [form.body]
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  useEffect(() => {
    if (!show) {
      setForm(emptyForm);
      setEditingId(null);
      setMode("list");
      setTemplateToDelete(null);
    }
  }, [show]);

  const openCreate = () => {
    resetForm();
    setMode("form");
  };

  const openEdit = (template: CustomTemplate) => {
    setForm({
      name: template.name,
      category: template.category,
      language: template.language,
      body: template.body,
      body_samples: template.body_samples ?? [],
      header: template.header,
      header_sample_url: template.header_sample_url ?? "",
      buttons: formButtonsOf(template.buttons),
    });
    setEditingId(template.id);
    setMode("form");
  };

  const wrapSelection = (marker: string) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd, value } = textarea;
    const selected = value.slice(selectionStart, selectionEnd) || "";
    const next = `${value.slice(
      0,
      selectionStart
    )}${marker}${selected}${marker}${value.slice(selectionEnd)}`;
    setForm((prev) => ({ ...prev, body: next }));
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = selectionStart + marker.length;
      textarea.selectionEnd = selectionEnd + marker.length;
    });
  };

  const insertVariable = () => {
    const textarea = bodyRef.current;
    const token = `{{${placeholders.count + 1}}}`;
    if (!textarea) {
      setForm((prev) => ({ ...prev, body: `${prev.body}${token}` }));
      return;
    }
    const { selectionStart, value } = textarea;
    const next = `${value.slice(0, selectionStart)}${token}${value.slice(
      selectionStart
    )}`;
    setForm((prev) => ({ ...prev, body: next }));
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = selectionStart + token.length;
      textarea.selectionEnd = selectionStart + token.length;
    });
  };

  const buildPayload = () => {
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      category: form.category,
      language: form.language,
      body: form.body.trim(),
      header: form.header,
    };
    if (form.header === "image")
      payload.header_sample_url = form.header_sample_url.trim();
    // Meta needs one example per variable, or the auto-filled "sample_1", "sample_2" get rejected.
    if (placeholders.count > 0) {
      payload.body_samples = Array.from(
        { length: placeholders.count },
        (_, i) => (form.body_samples[i] ?? "").trim()
      );
    }
    // CTA buttons only on utility; marketing keeps its opt-out (added server-side).
    if (form.category === "utility" && form.buttons.length > 0) {
      payload.buttons = form.buttons.map((b) => {
        if (b.type === "payment")
          return { type: "payment", title: b.title.trim() };
        if (b.type === "phone")
          return {
            type: "phone",
            title: b.title.trim(),
            phone: b.phone.trim(),
          };
        return {
          type: "url",
          title: b.title.trim(),
          url: b.url.trim(),
          dynamic_suffix: b.dynamic_suffix,
          ...(b.dynamic_suffix && b.url_sample_suffix.trim()
            ? { url_sample_suffix: b.url_sample_suffix.trim() }
            : {}),
        };
      });
    }
    return payload;
  };

  const activeButtons = form.category === "utility" ? form.buttons : [];
  const buttonsValid =
    activeButtons.length <= MAX_CTA_BUTTONS &&
    activeButtons.filter(
      (b) => b.type === "payment" || (b.type === "url" && b.dynamic_suffix)
    ).length <= 1 &&
    activeButtons.every((b) => {
      const titleOk =
        b.title.trim().length > 0 && b.title.trim().length <= BUTTON_TITLE_MAX;
      if (!titleOk) return false;
      if (b.type === "payment") return true; // the address is built for us
      return b.type === "url"
        ? HTTPS_PATTERN.test(b.url.trim())
        : E164_PATTERN.test(b.phone.trim());
    });

  const addButton = () => {
    if (form.buttons.length >= MAX_CTA_BUTTONS) return;
    setForm((prev) => ({
      ...prev,
      buttons: [...prev.buttons, { ...emptyButton }],
    }));
  };
  const removeButton = (index: number) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index),
    }));
  };
  const updateButton = (index: number, patch: Partial<FormButton>) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i) =>
        i === index ? { ...b, ...patch } : b
      ),
    }));
  };

  // Meta/WhatsApp require snake_case template names (3-60 lowercase letters, digits, underscores).
  const nameTrimmed = form.name.trim();
  const nameValid = /^[a-z0-9_]{3,60}$/.test(nameTrimmed);
  const nameHasInvalidChars =
    nameTrimmed.length > 0 && /[^a-z0-9_]/.test(nameTrimmed);

  const samplesComplete = Array.from({ length: placeholders.count }).every(
    (_, i) => (form.body_samples[i] ?? "").trim().length > 0
  );

  const bodyTrimmed = form.body.trim();
  const bodyEdgeVariable = bodyEdgesOnVariable(bodyTrimmed);

  const canSave =
    nameValid &&
    form.body.trim().length > 0 &&
    placeholders.valid &&
    !bodyEdgeVariable &&
    samplesComplete &&
    buttonsValid &&
    (form.header !== "image" || form.header_sample_url.trim().length > 0);

  const handleSave = () => {
    const payload = buildPayload();
    if (editingId) {
      updateTemplate(
        {
          customer_identification,
          entity_identification,
          template_id: editingId,
          data: payload,
        },
        {
          onSuccess: () => {
            resetForm();
            setMode("list");
          },
        }
      );
    } else {
      createTemplate(
        { customer_identification, entity_identification, data: payload },
        {
          onSuccess: () => {
            resetForm();
            setMode("list");
          },
        }
      );
    }
  };

  const k = (suffix: string) =>
    `dashboard.entities.viewEntityUser.sendWhatsApp.manageTemplates.${suffix}`;
  const busy =
    isCreating || isUpdating || isSubmitting || isDeleting || isRefreshing;

  const confirmDelete = () => {
    if (!templateToDelete) return;
    deleteTemplate(
      {
        customer_identification,
        entity_identification,
        template_id: templateToDelete.id,
      },
      {
        onSuccess: () => setTemplateToDelete(null),
        onError: () => setTemplateToDelete(null),
      }
    );
  };

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="xl"
        title={t(k("title"))}
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            {mode === "form" ? (
              <>
                <CustomButton
                  variant="secondary"
                  onClick={() => {
                    resetForm();
                    setMode("list");
                  }}
                  disabled={busy}
                  title={t("common.cancel") as string}
                />
                <CustomButton
                  variant="primary"
                  onClick={handleSave}
                  isLoading={isCreating || isUpdating}
                  disabled={!canSave || busy}
                  title={t(k("save")) as string}
                />
              </>
            ) : (
              <div className="flex w-full justify-end">
                <CustomButton
                  variant="primary"
                  onClick={openCreate}
                  disabled={remaining <= 0 || busy}
                  tooltip={
                    remaining <= 0
                      ? (t(k("limitReached")) as string)
                      : undefined
                  }
                  comp={
                    <div className="flex items-center gap-2">
                      <FaPlus size={12} />
                      <span>{t(k("addCta"))}</span>
                    </div>
                  }
                />
              </div>
            )}
          </div>
        }
      >
        {mode === "form" ? (
          <div className="space-y-4">
            {/* Creating and editing looked identical: say which one this is. */}
            <p className="border-b border-gray-100 pb-2 text-sm font-medium text-gray-700">
              {editingId ? (
                <NoTranslate>
                  {t(k("formTitleEdit"), { name: form.name })}
                </NoTranslate>
              ) : (
                t(k("formTitleNew"))
              )}
            </p>

            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {t(k("formExplanation"))}
            </p>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                {t(k("nameLabel"))}
              </span>
              <input
                type="text"
                className={fieldClass}
                maxLength={60}
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder={t(k("namePlaceholder")) as string}
                aria-invalid={nameHasInvalidChars}
              />
              {nameHasInvalidChars ? (
                <span className="mt-1 block text-xs text-red-600">
                  {t(k("nameInvalid"))}
                </span>
              ) : (
                <span className="mt-1 block text-xs text-gray-500">
                  {t(k("nameHint"))}
                </span>
              )}
            </label>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  {t(k("categoryLabel"))}
                </span>
                <select
                  className={fieldClass}
                  value={form.category}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      category: e.target.value as "marketing" | "utility",
                    }))
                  }
                >
                  <option value="marketing">{t(k("categoryMarketing"))}</option>
                  <option value="utility">{t(k("categoryUtility"))}</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  {t(k("languageLabel"))}
                </span>
                <select
                  className={fieldClass}
                  value={form.language}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      language: e.target.value as "fr" | "en",
                    }))
                  }
                >
                  <option value="fr">FR</option>
                  <option value="en">EN</option>
                </select>
              </label>
            </div>

            <div className="block">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t(k("bodyLabel"))}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => wrapSelection("*")}
                    className="rounded border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100"
                    aria-label={t(k("bold")) as string}
                  >
                    <FaBold size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => wrapSelection("_")}
                    className="rounded border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100"
                    aria-label={t(k("italic")) as string}
                  >
                    <FaItalic size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={insertVariable}
                    className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                  >
                    {t(k("addVariable"))}
                  </button>
                </div>
              </div>
              <textarea
                ref={bodyRef}
                className={`${fieldClass} min-h-[120px]`}
                maxLength={1024}
                value={form.body}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, body: e.target.value }))
                }
                placeholder={t(k("bodyPlaceholder")) as string}
              />
              <span className="mt-1 block text-xs text-gray-500">
                {t(k("bodyHint"))}
              </span>
              {!placeholders.valid && (
                <span className="mt-1 block text-xs text-red-600">
                  {t(k("variablesGap"))}
                </span>
              )}
              {placeholders.valid && bodyEdgeVariable && (
                <span className="mt-1 block text-xs text-red-600">
                  {t(k("bodyEdgeVariable"))}
                </span>
              )}
            </div>

            {placeholders.valid && placeholders.count > 0 && (
              <div>
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  {t(k("samplesLabel"))}
                </span>
                <span className="mb-2 block text-xs text-gray-500">
                  {t(k("samplesHint"))}
                </span>
                <div className="space-y-2">
                  {Array.from({ length: placeholders.count }).map(
                    (_, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="w-12 shrink-0 text-xs font-medium text-gray-500">{`{{${
                          index + 1
                        }}}`}</span>
                        <input
                          type="text"
                          className={fieldClass}
                          maxLength={300}
                          value={form.body_samples[index] ?? ""}
                          onChange={(e) =>
                            setForm((prev) => {
                              const next = [...prev.body_samples];
                              next[index] = e.target.value;
                              return { ...prev, body_samples: next };
                            })
                          }
                          placeholder={t(k("samplePlaceholder")) as string}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                {t(k("headerLabel"))}
              </span>
              <select
                className={fieldClass}
                value={form.header}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    header: e.target.value as "none" | "image",
                  }))
                }
              >
                <option value="none">{t(k("headerNone"))}</option>
                <option value="image">{t(k("headerImage"))}</option>
              </select>
            </label>

            {form.header === "image" && (
              <div className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  {t(k("headerSampleLabel"))}
                </span>
                <WhatsAppImageUpload
                  value={form.header_sample_url}
                  onUploaded={(url) =>
                    setForm((prev) => ({ ...prev, header_sample_url: url }))
                  }
                  onRemoved={() =>
                    setForm((prev) => ({ ...prev, header_sample_url: "" }))
                  }
                />
                <span className="mt-1 block text-xs text-gray-500">
                  {t(k("headerSampleHint"))}
                </span>
              </div>
            )}

            {form.category === "utility" && (
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {t(k("buttonsLabel"))}
                  </span>
                  {form.buttons.length < MAX_CTA_BUTTONS && (
                    <button
                      type="button"
                      onClick={addButton}
                      className="text-sm font-medium text-blue-600"
                    >
                      + {t(k("addButton"))}
                    </button>
                  )}
                </div>
                <span className="mb-2 block text-xs text-gray-500">
                  {t(k("buttonsHint"))}
                </span>
                <div className="space-y-3">
                  {form.buttons.map((button, index) => (
                    <div
                      key={index}
                      className="rounded-md border border-gray-200 p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <select
                          className={fieldClass}
                          value={button.type}
                          onChange={(e) =>
                            updateButton(index, {
                              type: e.target.value as FormButton["type"],
                            })
                          }
                        >
                          <option value="url">{t(k("buttonTypeUrl"))}</option>
                          <option value="phone">
                            {t(k("buttonTypePhone"))}
                          </option>
                          <option value="payment">
                            {t(k("buttonTypePayment"))}
                          </option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeButton(index)}
                          aria-label={t(k("removeButton")) as string}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                      <input
                        className={`${fieldClass} mt-2`}
                        maxLength={BUTTON_TITLE_MAX}
                        value={button.title}
                        onChange={(e) =>
                          updateButton(index, { title: e.target.value })
                        }
                        placeholder={t(k("buttonTitlePlaceholder")) as string}
                      />
                      {button.type === "payment" ? (
                        <span className="mt-1 block text-xs text-gray-500">
                          {t(k("buttonPaymentHint"))}
                        </span>
                      ) : button.type === "url" ? (
                        <>
                          <input
                            className={`${fieldClass} mt-2`}
                            value={button.url}
                            onChange={(e) =>
                              updateButton(index, { url: e.target.value })
                            }
                            placeholder={
                              button.dynamic_suffix
                                ? "https://maboutique.fr/suivi/"
                                : "https://..."
                            }
                          />
                          <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={button.dynamic_suffix}
                              onChange={(e) =>
                                updateButton(index, {
                                  dynamic_suffix: e.target.checked,
                                })
                              }
                            />
                            {t(k("buttonDynamic"))}
                          </label>
                          {button.dynamic_suffix && (
                            <>
                              <span className="mt-1 block text-xs text-gray-500">
                                {t(k("buttonDynamicHint"))}
                              </span>
                              <label className="mt-2 block">
                                <span className="mb-1 block text-xs font-medium text-gray-700">
                                  {t(k("buttonSampleLabel"))}
                                </span>
                                <input
                                  className={fieldClass}
                                  value={button.url_sample_suffix}
                                  onChange={(e) =>
                                    updateButton(index, {
                                      url_sample_suffix: e.target.value,
                                    })
                                  }
                                  placeholder="1234"
                                  maxLength={60}
                                />
                                <span className="mt-1 block text-xs text-gray-500">
                                  {t(k("buttonSampleHint"))}
                                </span>
                              </label>
                            </>
                          )}
                        </>
                      ) : (
                        <input
                          className={`${fieldClass} mt-2`}
                          value={button.phone}
                          onChange={(e) =>
                            updateButton(index, { phone: e.target.value })
                          }
                          placeholder="+41791112233"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700">
                {t(k("previewLabel"))}
              </span>
              <WhatsAppMessagePreview
                imageUrl={
                  form.header === "image"
                    ? form.header_sample_url || null
                    : null
                }
                body={form.body}
                isMarketing={form.category === "marketing"}
                buttons={
                  form.category === "utility"
                    ? previewButtonsOf(form.buttons)
                    : undefined
                }
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t(k("listExplanation"), { max: maxTemplates })}
            </p>

            {templates.length > 0 && (
              <div className="border-b border-gray-100 pb-3">
                <span className="text-sm font-medium tabular-nums text-gray-700">
                  {t(k("summaryRemaining"), {
                    count: remaining,
                    max: maxTemplates,
                  })}
                </span>
              </div>
            )}

            {isLoading ? (
              <p className="text-sm text-gray-500">{t(k("loading"))}</p>
            ) : templates.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                {t(k("empty"))}
              </p>
            ) : (
              <ul className="space-y-3">
                {templates.map((template) => (
                  <li
                    key={template.id}
                    className="rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <NoTranslate>
                          <span className="text-sm font-semibold text-gray-800">
                            {template.name}
                          </span>
                        </NoTranslate>
                        <span className="text-xs uppercase text-gray-400">
                          {template.language}
                        </span>
                        <span
                          className={`rounded-md px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[template.status] ??
                            STATUS_STYLES.draft
                          }`}
                        >
                          {t(k(`status.${template.status}`))}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {RESUBMITTABLE_STATUSES.includes(template.status) && (
                          <>
                            <CustomButton
                              variant="secondary"
                              size="xs"
                              onClick={() => openEdit(template)}
                              disabled={busy}
                              title={t(k("edit")) as string}
                            />
                            <CustomButton
                              variant="primary"
                              size="xs"
                              onClick={() =>
                                submitTemplate({
                                  customer_identification,
                                  entity_identification,
                                  template_id: template.id,
                                })
                              }
                              isLoading={isSubmitting}
                              disabled={busy}
                              title={t(k("submit")) as string}
                            />
                          </>
                        )}
                        {template.status === "pending" && (
                          <CustomButton
                            variant="secondary"
                            size="xs"
                            onClick={() =>
                              refreshStatus({
                                customer_identification,
                                entity_identification,
                                template_id: template.id,
                              })
                            }
                            isLoading={isRefreshing}
                            disabled={busy}
                            title={t(k("refresh")) as string}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setTemplateToDelete(template)}
                          disabled={busy}
                          className="rounded border border-gray-300 p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-50"
                          aria-label={t(k("delete")) as string}
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                    <NoTranslate>
                      <div className="mt-2">
                        <WhatsAppMessagePreview
                          imageUrl={
                            template.header === "image"
                              ? template.header_sample_url
                              : null
                          }
                          body={template.body}
                          isMarketing={template.category === "marketing"}
                          buttons={previewButtonsOf(template.buttons)}
                        />
                      </div>
                    </NoTranslate>
                    {(template.status === "approved" ||
                      template.status === "pending") && (
                      <p className="mt-2 text-xs text-gray-500">
                        {t(
                          k(
                            template.status === "approved"
                              ? "lockedApproved"
                              : "lockedPending"
                          )
                        )}
                      </p>
                    )}
                    {(template.status === "rejected" ||
                      template.status === "submission_failed") &&
                      template.rejection_reason && (
                        <p className="mt-2 text-xs text-red-600">
                          {t(k("rejectionReason"))}: {template.rejection_reason}
                        </p>
                      )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ModalShell>
      <ConfirmModal
        show={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        title={t(k("deleteTitle")) as string}
        description={
          t(k("deleteConfirm"), {
            name: templateToDelete?.name || "",
          }) as string
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={() => setTemplateToDelete(null)}
        confirmBtnName={t("common.delete") as string}
        confirmBtnFn={confirmDelete}
        ConfirmBtnColor="red"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageWhatsAppTemplatesModal;
